import { supabase, getActiveSchema } from '../../../../supabaseClient';
import { EventSchema, Event } from '@/types/zod.schemas';

/**
 * EVENT REPOSITORY
 * Specialized IO for the Unified Event Stream (v3.events).
 */

const getClient = () => {
    const schema = getActiveSchema();
    return supabase.schema(schema);
};

const EVENT_COLUMNS = 'id, user_id, type, status, start_time, end_time, label, summary, payload, preview_data, completed_session_id, sleep_log_id, mobility_log_id, nutrition_log_id, supplement_log_id, program_day_id, created_at, updated_at';

export const EventRepo = {
    /**
     * fetchEvents: Retrieves events for a specific user and date range.
     */
    async fetchEvents(userId: string, startDate?: string, endDate?: string) {
        let query = getClient()
            .from('events')
            .select(EVENT_COLUMNS)
            .eq('user_id', userId)
            .order('start_time', { ascending: false });

        if (startDate) query = query.gte('start_time', startDate);
        if (endDate) query = query.lte('start_time', endDate);

        const { data, error } = await query;
        
        // ZOD SHIELD: Validate incoming data with resilience
        const validEvents: Event[] = [];
        if (data) {
            data.forEach(item => {
                const result = EventSchema.safeParse(item);
                if (result.success) {
                    validEvents.push(result.data);
                } else {
                    console.warn("[EventRepo] Invalid event record found:", result.error.format(), item);
                }
            });
        }

        return { data: validEvents, error };
    },

    async registerPerformanceEvent(sessionId: string, consumeEventId: string | null = null) {
        return await getClient().rpc('register_performance_event', { 
            p_session_id: sessionId,
            p_consume_event_id: consumeEventId || undefined
        });
    },

    async registerRecoveryEvent(logId: string, consumeEventId: string | null = null) {
        return await getClient().rpc('register_recovery_event', { 
            p_log_id: logId,
            p_consume_event_id: consumeEventId || undefined
        });
    },

    async autoSkipPastEvents() {
        return await getClient().rpc('auto_skip_past_events');
    },

    async updateUnifiedEventStatus(eventId: string, status: string) {
        const { data, error } = await getClient()
            .from('events')
            .update({ status } as any)
            .eq('id', eventId)
            .select(EVENT_COLUMNS)
            .maybeSingle();

        if (data) EventSchema.safeParse(data);
        return { data, error };
    },

    async updateUnifiedEvent(eventId: string, payload: Partial<Event>) {
        const { data, error } = await getClient()
            .from('events')
            .update(payload as any)
            .eq('id', eventId)
            .select(EVENT_COLUMNS)
            .maybeSingle();

        if (data) EventSchema.safeParse(data);
        return { data, error };
    },

    async createUnifiedEvent(payload: Partial<Event>) {
        const { data, error } = await getClient()
            .from('events')
            .insert([payload] as any)
            .select(EVENT_COLUMNS)
            .maybeSingle();

        if (data) EventSchema.safeParse(data);
        return { data, error };
    },

    async deleteUnifiedEvent(eventId: string) {
        return await getClient()
            .from('events')
            .delete()
            .eq('id', eventId);
    },

    async fetchCalendarEvents(startDate: string, endDate: string) {
        const { data, error } = await getClient()
            .from('events')
            .select(`
                ${EVENT_COLUMNS},
                program_days ( label )
            `)
            .gte('start_time', `${startDate}T00:00:00`)
            .lte('start_time', `${endDate}T23:59:59`)
            .order('start_time', { ascending: true });

        // Calendar events include joined program_days, so we partial parse
        return { data, error };
    },

    async deleteEvent(eventId: string) {
        return await this.deleteUnifiedEvent(eventId);
    }
};
